import { Injectable, inject } from '@angular/core';
import { NgxPlaidLinkService, PlaidLinkHandler } from 'ngx-plaid-link';
import { UserService } from '../user/user.service';
import { Subject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

export interface PlaidConfig {
  onSuccess?: (publicToken: string, metadata: any) => void;
  onExit?: (error: any, metadata: any) => void;
  onEvent?: (eventName: string, metadata: any) => void;
  includeTransactions?: boolean;
}

export interface PlaidInstitutionData {
  institution_name: string;
  name: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class PlaidService {
  userService = inject(UserService);
  plaidLinkService = inject(NgxPlaidLinkService);
  plaidLinkHandler: PlaidLinkHandler | null = null;
  selectedInstitutionData: PlaidInstitutionData | null = null;
  institutionSelected$ = new Subject<PlaidInstitutionData>();
  institutionSelected: Observable<PlaidInstitutionData> = this.institutionSelected$.asObservable();

  async initializePlaidLink(config?: PlaidConfig): Promise<void> {
    try {
      const response: any = await this.getPlaidLinkToken();
      const linkToken = response.link_token;

      this.plaidLinkService
        .createPlaid(
          Object.assign(
            {},
            { token: linkToken },
            {
              onSuccess: (token, metadata) => {
                this.onPlaidConnectionSuccess(token, metadata, config);
              },
              onExit: (error, metadata) => {
                if (config?.onExit) {
                  config.onExit(error, metadata);
                }
              },
              onEvent: (eventName, metadata) => {
                this.handlePlaidEvents(eventName, metadata);
                if (config?.onEvent) {
                  config.onEvent(eventName, metadata);
                }
              },
            }
          )
        )
        .then((handler: PlaidLinkHandler) => {
          this.plaidLinkHandler = handler;
          this.plaidLinkHandler.open();
        });
    } catch (error) {
      console.error('Error initializing Plaid Link:', error);
      throw error;
    }
  }

  async initializePlaidLinkUpdateMode(accessToken: string, tokenId: string, config?: PlaidConfig): Promise<void> {
    try {
      const response: any = await this.getPlaidUpdateModeLinkToken(accessToken);
      const linkToken = response.link_token;
      this.plaidLinkService
        .createPlaid(
          Object.assign(
            {},
            { token: linkToken },
            {
              onSuccess: (token, metadata) => {
                this.onPlaidReConnectionSuccess(tokenId, metadata, config);
              },
              onExit: (error, metadata) => {
                if (config?.onExit) {
                  config.onExit(error, metadata);
                }
              },
              onEvent: (eventName, metadata) => {
                this.handlePlaidEvents(eventName, metadata);
                if (config?.onEvent) {
                  config.onEvent(eventName, metadata);
                }
              },
            }
          )
        )
        .then((handler: PlaidLinkHandler) => {
          this.plaidLinkHandler = handler;
          this.plaidLinkHandler.open();
        });
    } catch (error) {
      console.error('Error initializing Plaid Link Update Mode:', error);
      throw error;
    }
  }

  private getPlaidLinkToken(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService
        .getPlaidLinkToken()
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        });
    });
  }

  private getPlaidUpdateModeLinkToken(accessToken: string, options?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService
        .getPlaidUpdateModeLinkToken({
          access_token: accessToken,
          enableAccountSelection: options?.enableAccountSelection,
          additionalProducts: options?.additionalProducts,
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        });
    });
  }

  private updateUserAccountPlaidTokenConnectionStatus(tokenId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService
        .updateUserAccountPlaidTokenConnectionStatus({
          id: tokenId,
          user_account_status: 'connected',
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        });
    });
  }

  exchangePlaidPublicToken(publicToken: string, institutionId: string, includeTransactions = false): Promise<any> {
    return new Promise((resolve, reject) => {
      this.userService
        .exchangePlaidPublicToken({
          public_token: publicToken,
          include_transactions: includeTransactions,
          institution_id: institutionId,
        })
        .pipe(take(1))
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        });
    });
  }

  private async onPlaidConnectionSuccess(publicToken: string, metadata: any, config?: PlaidConfig): Promise<void> {
    const includeTransactions = config?.includeTransactions ?? false;
    const response: any = await this.exchangePlaidPublicToken(publicToken, metadata?.institution?.institution_id || null, includeTransactions);
    if (response.success && config?.onSuccess) {
      config.onSuccess(publicToken, metadata);
    }
  }

  private async onPlaidReConnectionSuccess(tokenId: string, metadata: any, config?: PlaidConfig): Promise<void> {
    const response: any = await this.updateUserAccountPlaidTokenConnectionStatus(tokenId);
    if (response.status === 'success' && config?.onSuccess) {
      config.onSuccess(null, metadata);
    }
  }

  private handlePlaidEvents(eventName: string, metadata: any): void {
    switch (eventName) {
      case 'SELECT_INSTITUTION':
        this.selectedInstitutionData = {
          ...metadata,
          name: metadata.institution_name,
        };
        this.institutionSelected$.next(this.selectedInstitutionData);
        break;
      default:
        break;
    }
  }

  getSelectedInstitutionData(): PlaidInstitutionData | null {
    return this.selectedInstitutionData;
  }

  clearSelectedInstitutionData(): void {
    this.selectedInstitutionData = null;
  }

  closePlaidLink(): void {
    if (this.plaidLinkHandler) {
      this.plaidLinkHandler.exit();
      this.plaidLinkHandler = null;
    }
  }
}
